import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Inbox as InboxIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export default function Inbox() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState("");

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);
  };

  const { data: messages, isLoading } = useQuery({
    queryKey: ["messages", currentUser?.id],
    queryFn: async () => {
      // Fetch messages where user is sender or recipient
      const { data: messagesData, error } = await supabase
        .from("messages")
        .select("*")
        .or(`sender_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      // Fetch profiles for senders/recipients
      const userIds = [...new Set(messagesData.flatMap(m => [m.sender_id, m.recipient_id]))];
      const profilesMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("public_profiles")
          .select("id, username, avatar_url")
          .in("id", userIds);
        for (const p of profiles || []) {
          profilesMap[p.id] = p;
        }
      }
      
      return messagesData.map(m => ({
        ...m,
        sender: profilesMap[m.sender_id] || { username: 'Unknown', avatar_url: null },
        recipient: profilesMap[m.recipient_id] || { username: 'Unknown', avatar_url: null }
      }));
    },
    enabled: !!currentUser,
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (messageId) => {
      const { error } = await supabase
        .from("messages")
        .update({ is_read: true })
        .eq("id", messageId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["messages"]);
    },
  });

  const sendReplyMutation = useMutation({
    mutationFn: async ({ recipientId, content, threadId }) => {
      const { error } = await supabase
        .from("messages")
        .insert({
          sender_id: currentUser.id,
          recipient_id: recipientId,
          content,
          thread_id: threadId
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["messages"]);
      setReplyText("");
      toast.success("Reply sent!");
    },
    onError: () => {
      toast.error("Failed to send reply");
    },
  });

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    if (!message.is_read && message.recipient_id === currentUser?.id) {
      markAsReadMutation.mutate(message.id);
    }
  };

  const handleSendReply = () => {
    if (!replyText.trim() || !selectedMessage) return;
    
    const recipientId = selectedMessage.sender_id === currentUser.id 
      ? selectedMessage.recipient_id 
      : selectedMessage.sender_id;
    
    sendReplyMutation.mutate({
      recipientId,
      content: replyText,
      threadId: selectedMessage.thread_id || selectedMessage.id,
    });
  };

  if (isLoading || !currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const unreadCount = messages?.filter(m => !m.is_read && m.recipient_id === currentUser.id).length || 0;

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <InboxIcon className="w-8 h-8" />
            Inbox
            {unreadCount > 0 && (
              <span className="bg-primary text-primary-foreground rounded-full px-3 py-1 text-sm">
                {unreadCount} unread
              </span>
            )}
          </h1>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-2">
            {!messages || messages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No messages yet
              </div>
            ) : (
              messages.map((message) => {
                const isFromMe = message.sender_id === currentUser.id;
                const otherUser = isFromMe ? message.recipient : message.sender;
                const isUnread = !message.is_read && message.recipient_id === currentUser.id;
                
                return (
                  <div
                    key={message.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedMessage?.id === message.id 
                        ? 'bg-primary/10 border-primary' 
                        : 'bg-card border-border hover:border-primary'
                    } ${isUnread ? 'font-semibold' : ''}`}
                    onClick={() => handleMessageClick(message)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <img
                        src={otherUser?.avatar_url || "/placeholder.svg"}
                        alt={otherUser?.username}
                        className="w-10 h-10 rounded-full"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-card-foreground truncate">
                          {otherUser?.username}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(message.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      {isUnread && (
                        <div className="w-2 h-2 bg-primary rounded-full"></div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {message.content}
                    </p>
                  </div>
                );
              })
            )}
          </div>

          <div className="md:col-span-2">
            {selectedMessage ? (
              <div className="bg-card p-6 rounded-lg border border-border h-full flex flex-col">
                <div className="flex items-center gap-3 pb-4 border-b border-border mb-4">
                  <img
                    src={
                      (selectedMessage.sender_id === currentUser.id 
                        ? selectedMessage.recipient?.avatar_url 
                        : selectedMessage.sender?.avatar_url) || "/placeholder.svg"
                    }
                    alt="User"
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <div className="font-semibold text-card-foreground">
                      {selectedMessage.sender_id === currentUser.id 
                        ? selectedMessage.recipient?.username 
                        : selectedMessage.sender?.username}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto mb-4">
                  <p className="text-card-foreground whitespace-pre-wrap">
                    {selectedMessage.content}
                  </p>
                </div>

                <div className="space-y-2">
                  <Textarea
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                  />
                  <Button onClick={handleSendReply} disabled={!replyText.trim()} tooltip={!replyText.trim() ? "Enter a message to reply" : "Send your reply message"}>
                    <Send className="w-4 h-4 mr-2" />
                    Send Reply
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-card p-12 rounded-lg border border-border h-full flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <InboxIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Select a message to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
